import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, BookOpen, ArrowUpRight, Sparkles } from "lucide-react";
import { paymentsApi } from "@/lib/api";
import { PublishingNav } from "@/components/publishing/PublishingShared";

export default function PurchaseSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", kind: null, book_slug: null });
  const [polling, setPolling] = useState(true);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setPolling(false); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const s = await paymentsApi.status(sessionId);
        setStatus(s);
        if (s.payment_status === "paid" || s.payment_status === "expired" || s.payment_status === "failed") {
          setPolling(false);
          return;
        }
      } catch {}
      attemptsRef.current += 1;
      if (attemptsRef.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setPolling(false);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isPaid = status.payment_status === "paid";
  const isCollection = status.kind === "collection";
  const isSubscription = status.kind === "subscription";
  const isIndividual = status.kind === "individual";

  return (
    <div className="bg-primary min-h-screen">
      <div className="noise-overlay" />
      <PublishingNav />

      <section data-testid="purchase-success" className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-jcrown-gold/10 blur-3xl" />
        </div>

        <div className="relative z-10 max-w-2xl w-full text-center">
          {polling ? (
            <>
              <div className="w-8 h-8 border-2 border-jcrown-gold border-t-transparent rounded-full animate-spin mx-auto mb-8" />
              <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body mb-4">Confirming Payment</p>
              <h1 className="font-heading text-white text-4xl md:text-5xl tracking-tighter uppercase mb-6">
                Just a moment...
              </h1>
              <p className="text-white/50 font-body">We're verifying your payment with Stripe.</p>
            </>
          ) : isPaid ? (
            <>
              <Crown className="w-10 h-10 text-jcrown-gold mx-auto mb-6" />
              <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
                {isCollection ? "The Complete Library Is Yours" : isSubscription ? "Library Pass Active" : "Book Unlocked"}
              </p>
              <h1 className="font-heading text-white text-4xl md:text-6xl tracking-tighter uppercase mb-6 leading-[0.9]">
                {isCollection ? "The Complete Johnny Crown Collection Is Now Yours" : "Welcome to the Library"}
              </h1>
              <p className="text-white/60 font-body text-base md:text-lg mb-12 max-w-lg mx-auto">
                {isSubscription
                  ? "Your Library Pass is active. Enjoy full access to the growing Johnny Crown Library."
                  : "Your book has been added to My Library."
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {isIndividual && status.book_slug ? (
                  <Link
                    to={`/read/${status.book_slug}`}
                    data-testid="success-start-reading"
                    className="group bg-jcrown-gold text-jcrown-dark px-8 py-4 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 inline-flex items-center justify-center gap-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    Start Reading
                  </Link>
                ) : null}
                <Link
                  to="/my-library"
                  data-testid="success-view-library"
                  className={`inline-flex items-center justify-center gap-2 px-8 py-4 font-body text-xs tracking-widest uppercase transition-all active:scale-95 ${
                    isIndividual && status.book_slug
                      ? "border border-white/20 text-white hover:border-white hover:bg-white/5"
                      : "bg-jcrown-gold text-jcrown-dark hover:bg-white hover:text-black"
                  }`}
                >
                  {isCollection || isSubscription ? "Enter My Library" : "View My Library"}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-white/70 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
                Payment {status.payment_status || "Pending"}
              </p>
              <h1 className="font-heading text-white text-4xl md:text-5xl tracking-tighter uppercase mb-6">
                Something went wrong
              </h1>
              <p className="text-white/50 font-body mb-12">
                We couldn't confirm your payment. If you were charged, please contact support — your library will be updated shortly.
              </p>
              <Link
                to="/books"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all"
              >
                Back to the Library
              </Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
